import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModalPanel from 'UIComponents/dialog/UIModalPanel';
import { H2 } from 'UIComponents/elements/headings';
import UIForm from 'UIComponents/form/UIForm';
import UIButton from 'UIComponents/button/UIButton';
import { useCallback, useEffect, useState } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UISection from 'UIComponents/section/UISection';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import { NewPropertiesForm } from '../form/NewPropertiesForm';
import ConditionalPropertiesPanelProgress from './ConditionalPropertiesPanelProgress';
import ConditionalPropertiesPanelSkeletonLoader from './ConditionalPropertiesPanelSkeletonLoader';
import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import { logPageView, trackConditionalPropertiesPanelSubmit } from '../utils/usageTrackingUtils';
import ConditionalPropertiesPanelDependentSection from './ConditionalPropertiesPanelDependentSection';
import enviro from 'enviro';
import PortalIdParser from 'PortalIdParser';
import memoizeOne from 'react-utils/memoizeOne';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { buildPropertiesFormInitializingState } from '../form/validation/utils/status';
import { PropertiesProvider } from '../context/PropertiesProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BOUNDARY_NAME = 'CustomerDataProperties_ConditionalPropertiesPanelError';
export const REFRESH_LIMIT = 3;
const isRevenuePortal = () => {
  const BETPortalId = enviro.isQa() ? 99535353 : 53;
  return BETPortalId === PortalIdParser.get();
};
const isRevenuePortalMemoized = memoizeOne(isRevenuePortal);
const getErrorComponent = ({
  onClose
}) => {
  return /*#__PURE__*/_jsxs(UIErrorMessage, {
    title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "conditionalPropertiesPanel.errorTitle"
    }),
    children: [/*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "conditionalPropertiesPanel.errorDescriptionErrorBoundary"
      })
    }), /*#__PURE__*/_jsx(UIButton, {
      use: "tertiary-light",
      onClick: onClose,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "conditionalPropertiesPanel.closeButton"
      })
    })]
  });
};
const ConditionalPropertiesPanel = ({
  objectId,
  objectTypeId,
  triggerPropertyDefinition,
  properties,
  onApply,
  onCancel,
  defaultValues,
  getAdditionalPropertyValue,
  showAutoSaveText = false,
  renderInput
}) => {
  useComponentMountMetric('ConditionalPropertiesPanel');
  const [draftValues, setDraftValues] = useState(defaultValues || {});
  const [formStatus, setFormStatus] = useState(buildPropertiesFormInitializingState());
  const [formKey, setFormKey] = useState(0);
  useEffect(() => {
    logPageView({
      subscreen: 'conditional-properties-panel'
    });
  }, []);
  const handleFormStatusChange = useCallback(updatedStatus => {
    setFormStatus(updatedStatus);
  }, [setFormStatus]);
  const handleChange = useCallback(changes => {
    setDraftValues(changes);
  }, [setDraftValues]);
  const handleRefreshClick = useCallback(() => {
    if (formKey >= REFRESH_LIMIT) {
      return;
    }
    setFormStatus(buildPropertiesFormInitializingState());
    setFormKey(prev => prev + 1);
  }, [formKey]);
  const renderDependentSection = useCallback((input, passthroughProps) => {
    return /*#__PURE__*/_jsx(ConditionalPropertiesPanelDependentSection, {
      input: input,
      passthroughProps: passthroughProps,
      renderInput: renderInput
    });
  }, [renderInput]);
  const isSaveable = formStatus.saveable;
  const handleSubmit = useCallback(() => {
    trackConditionalPropertiesPanelSubmit();
    onApply(draftValues);
  }, [draftValues, onApply]);
  return /*#__PURE__*/_jsx(PropertiesProvider, {
    children: /*#__PURE__*/_jsx(UIModalPanel, {
      width: '520px',
      "data-test-id": "conditional-properties-panel",
      children: /*#__PURE__*/_jsx(ErrorBoundary, {
        ErrorComponent: getErrorComponent,
        errorProps: {
          onClose: onCancel
        },
        boundaryName: BOUNDARY_NAME,
        showRefreshAlert: false,
        children: /*#__PURE__*/_jsxs(UIForm, {
          onSubmit: evt => {
            evt.preventDefault(); // don't submit the whole page
            evt.stopPropagation(); // don't submit any parent forms either
            if (isSaveable) {
              handleSubmit();
            }
          },
          children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
            children: [/*#__PURE__*/_jsx(H2, {
              children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.title"
              })
            }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
              onClick: () => {
                onCancel();
              }
            })]
          }), formStatus.initializing && /*#__PURE__*/_jsx(ConditionalPropertiesPanelProgress, {}), /*#__PURE__*/_jsxs(UIDialogBody, {
            children: [/*#__PURE__*/_jsx(UISection, {
              children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.description",
                options: {
                  triggerPropertyLabel: triggerPropertyDefinition.label
                }
              })
            }), /*#__PURE__*/_jsxs(UISection, {
              children: [formStatus.initializing && /*#__PURE__*/_jsx(ConditionalPropertiesPanelSkeletonLoader, {}), formStatus.initializationFailed && /*#__PURE__*/_jsxs(UIErrorMessage, {
                "data-test-id": "conditional-properties-panel-error",
                title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                  message: "conditionalPropertiesPanel.errorTitle"
                }),
                children: [/*#__PURE__*/_jsx("p", {
                  children: formKey < REFRESH_LIMIT ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: 'conditionalPropertiesPanel.errorDescriptionWithRefresh'
                  }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: 'conditionalPropertiesPanel.errorDescriptionNoRefresh'
                  })
                }), formKey < REFRESH_LIMIT && /*#__PURE__*/_jsx("p", {
                  children: /*#__PURE__*/_jsx(UIButton, {
                    use: "transparent",
                    onClick: handleRefreshClick,
                    children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                      message: "conditionalPropertiesPanel.errorRefreshButton"
                    })
                  })
                })]
              }), !formStatus.initializationFailed && /*#__PURE__*/_jsx(NewPropertiesForm, {
                objectId: objectId,
                objectTypeId: objectTypeId,
                getSavedValue: getAdditionalPropertyValue,
                values: draftValues,
                onChange: handleChange,
                onStatusChange: handleFormStatusChange,
                properties: properties,
                alwaysShowConditionalProperties: true,
                alwaysShowConditionalPropertyOptionsWithErrors: true,
                renderDependentSection: renderDependentSection,
                showPropertyDescription: isRevenuePortalMemoized(),
                renderInput: renderInput
              }, formKey)]
            })]
          }), /*#__PURE__*/_jsxs(UIDialogFooter, {
            children: [/*#__PURE__*/_jsx(UIButton, {
              use: "primary",
              type: "submit",
              disabled: !isSaveable,
              children: showAutoSaveText ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.saveButton"
              }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.applyButton"
              })
            }), /*#__PURE__*/_jsx(UIButton, {
              use: "tertiary-light",
              onClick: () => {
                onCancel();
              },
              children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.cancelButton"
              })
            })]
          })]
        })
      })
    })
  });
};
export default ConditionalPropertiesPanel;